import { useQueries } from '@tanstack/react-query'

import seatsAPI from '@api/seats'
import { ApiError } from '@lib/api'
import dateUtils from '@lib/date'
import seatSelectionUtils, { BuildSeatsProps } from '@lib/seatSelection'
import utils from '@lib/utils'
import { useParams } from '@stores/params'

interface UseSeatsQueriesProps {
  connections: Connection[] | undefined
  options?: BuildSeatsProps
  enabled?: boolean
}

type Refetch = () => void

const formatDate = (date: string): string => dateUtils.formatDateISO(dateUtils.parse(date, 'UTC'))
const findSeatIndex = (connections: Connection[] | undefined, seats: Seat.Data): number | undefined => {
  const connectionSegments = connections?.flatMap(connection => connection.segments)

  return connectionSegments?.findIndex(
    connection =>
      formatDate(connection.departureTime) === formatDate(seats.segment.departureTime) &&
      formatDate(connection.arrivalTime) === formatDate(seats.segment.arrivalTime),
  )
}

interface Meta {
  isLoading: boolean
  error: ApiError | null | undefined
  status: string[]
}

export type UseSeatsQueriesHook = [Seat.Data[], Meta, Refetch]

const useSeatsQueries = ({ connections = [], enabled, options }: UseSeatsQueriesProps): UseSeatsQueriesHook => {
  const [params] = useParams()

  const combinedQuery = useQueries({
    queries: connections.map(value => {
      // TODO: get the correct fare per connection: https://distribusion.atlassian.net/browse/OWL-3957
      const parameters = { ...seatSelectionUtils.buildSeatsParams(value, params, options) }

      return {
        queryKey: ['seats', value?.id, options?.fareClass],
        queryFn: () => seatsAPI.load(parameters),
        enabled: !!value && enabled && Object.values(parameters).every(item => item),
        staleTime: 0,
        gcTime: 0,
      }
    }),
    combine: results => {
      return {
        data: utils.array.compact(
          results.flatMap(({ data: seatsPerConnection }, connectionIndex) =>
            seatsPerConnection?.map(
              (segmentSeats, segmentIndex) =>
                segmentSeats &&
                ({
                  ...segmentSeats,
                  connection: connections[connectionIndex]!,
                  segment: { ...segmentSeats.segment, index: findSeatIndex(connections, segmentSeats) ?? segmentIndex },
                } satisfies Seat.Data),
            ),
          ),
        ),
        error: results.find(Boolean)?.error,
        status: results.map(({ status }) => status),
        isLoading: results.some(({ isLoading, isFetching }) => isLoading || isFetching),
        refetch: () => results.forEach(result => result.refetch()),
      }
    },
  })

  const meta = {
    isLoading: combinedQuery.isLoading,
    status: combinedQuery.status,
    error: combinedQuery.error as ApiError,
  }

  return [combinedQuery.data, meta, combinedQuery.refetch]
}

export default useSeatsQueries
